import Cookies from 'js-cookie';
import { CookiesProvider } from 'react-cookie';
import ServerCookies from 'cookies';

import { getToken } from '../config/api';
import type { AppProps } from 'next/app';
import { useEffect } from 'react';
import { Provider } from 'react-redux';
import Intercom from '@intercom/messenger-js-sdk';

import { checkAuth } from 'flaivy-react/actions/auth.actions';
import GreetingModal from '../components/GreetingModal';
import HeadElement from 'components/HeadElement';

import { loadMyFavoriteArticles } from '../shared/actions/my-favorite-articles.action';

// @ts-ignore
import ErrorBoundary from 'flaivy-react/components/ErrorBoundary';
import { MountPoint } from 'flaivy-react/components/Confirmation';

// @ts-ignore
import CartProgressBar from 'flaivy-react/components/cart/CartProgressBars';

import '/node_modules/react-datepicker/dist/react-datepicker.css';
import '/node_modules/@fortawesome/fontawesome-svg-core/styles.css';
import Navbar from '../components/navbar/Navbar';
import { loadConfig } from '../shared/actions/shop-config.action';
import type { NextPage } from 'next';

import _ from 'lodash';
import type { Store } from 'redux';
import withReduxStore from '../shared/with-redux-store';
import Footer from '../components/Footer';
import { toggleLoginModal } from 'shared/actions/login-modal.action';
import HydrateLocalStorageProvider from '../components/HydrateLocalStorageProvider';

import { SHOP_REDIRECT_AFTER_LOGIN_KEY } from '../config/api';
import { loadPersistedMultiCart } from 'flaivy-react/actions/persisted-multi-cart.actions';
import Script from 'next/script';
interface ReduxProps extends AppProps {
  reduxStore: Store;
  asPath: string;
  isDevCluster: boolean;
  pageProps: object;
  pathName: any;
  styles: any;
}

// @ts-ignore
const App: NextPage<ReduxProps> = ({
  Component,
  pageProps,
  reduxStore,
  asPath,
}) => {
  const shopConfig = reduxStore.getState().shopConfig;
  const sessionUser = reduxStore.getState().authentication.sessionUser;

  useEffect(() => {
    const redirectCookie = Cookies.get(SHOP_REDIRECT_AFTER_LOGIN_KEY);
    if (redirectCookie) {
      // @ts-ignore
      reduxStore.dispatch(toggleLoginModal());
    }

    localStorage.removeItem('ZD-launcherLabelRemoved');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  shopConfig.chatConfig?.useChat &&
    shopConfig.chatConfig.chatIntegration === 'intercom' &&
    Intercom({
      // app_id: 'ey143zkf',
      app_id: shopConfig.chatConfig.intercomConfig.appId,
      api_base: 'https://api-iam.eu.intercom.io',
      background_color: shopConfig.chatConfig.intercomConfig.backgroundColor,
      action_color: shopConfig.chatConfig.intercomConfig.accentColor,
      name: sessionUser?.name,
      email: sessionUser?.email,
      user_id: sessionUser?.id,
      phone: sessionUser?.phone,
    });

  return (
    <Provider store={reduxStore}>
      <HydrateLocalStorageProvider>
        <CookiesProvider>
          <HeadElement shopConfig={shopConfig} asPath={asPath} />
          <GreetingModal />
          <Navbar asPath={asPath} />

          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <ErrorBoundary>
            <MountPoint></MountPoint>
            <Component {...{ ...pageProps, asPath }} />
          </ErrorBoundary>
          {shopConfig?.chatConfig?.useChat && (
            <Script
              src={`https://static.zdassets.com/ekr/snippet.js?key=${shopConfig?.chatConfig?.zendeskConfig?.zendeskKey}`}
              id="ze-snippet"
            />
          )}

          <CartProgressBar />
          <Footer />
        </CookiesProvider>
      </HydrateLocalStorageProvider>
    </Provider>
  );
};

// @ts-ignore
async function redirectToLogin({ res, req, asPath }) {
  const targetURL = '/';
  if (res) {
    // On the server,
    const cookieJar = new ServerCookies(req, res);
    cookieJar.set(SHOP_REDIRECT_AFTER_LOGIN_KEY, req.url, {
      httpOnly: false, // true by default
    });
    // we'll use an HTTP response to
    // redirect with the status code of our choice.
    // 307 is for temporary redirects.
    res.writeHead(307, { Location: targetURL });
    res.end();
  } else {
    //client side
    Cookies.set(SHOP_REDIRECT_AFTER_LOGIN_KEY, asPath, {
      httpOnly: false, // true by default
    });

    // We'll redirect to the external page using
    // `window.location`.
    // @ts-ignore
    window.location = targetURL;
    // While the page is loading, code execution will
    // continue, so we'll await a never-resolving
    // promise to make sure our page never
    // gets rendered.
    await new Promise(() => {});
  }
  return {};
}

// @ts-ignore
App.getInitialProps = async ({ Component, ctx }) => {
  const { req, pathname, asPath } = ctx;
  if (asPath === '/health') {
    return {};
  }

  let host;
  if (req) {
    // Server side rendering
    // eslint-disable-next-line
    host = req.headers.host;
  } else {
    host = window.location.hostname;
  }

  const { dispatch } = ctx.reduxStore;
  const { authentication, shopConfig } = ctx.reduxStore.getState();
  if (_.isEmpty(shopConfig)) {
    await dispatch(loadConfig(req));
  }

  const hasAuthCookie = getToken(req);

  // old cookie
  if (_.isEmpty(authentication.sessionUser) && hasAuthCookie) {
    await dispatch(checkAuth(req));
  }

  const isAppPage = asPath && asPath.match(/\/app.*/);
  if (isAppPage && !hasAuthCookie) {
    await redirectToLogin(ctx);
    return;
  }

  const isLoggedIn =
    hasAuthCookie &&
    _.get(ctx.reduxStore.getState().authentication, 'isAuthenticated');

  if (
    isLoggedIn &&
    !_.size(ctx.reduxStore.getState().persistedMultiCart.carts)
  ) {
    await dispatch(loadPersistedMultiCart(req));
  }
  if (
    isLoggedIn &&
    !_.get(ctx.reduxStore.getState().persistedMultiCart, 'purchaseListId')
  ) {
    await dispatch(loadMyFavoriteArticles(req));
  }

  let pageProps = {};

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx);
  }

  return {
    pageProps,
    asPath,
    pathname,
    isDevCluster: _.includes(host, 'dev'),
  };
};

export default withReduxStore(App);